import axios from "axios";
import type { z } from "zod";

type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

/**
 * 서버 error 응답의 message 를 그대로 꺼낸다.
 * 사용자에게 노출 가능한 문구가 내려오는 API 전용이며, 없으면 fallback 을 쓴다.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const message = axios.isAxiosError<{ message?: string }>(error)
    ? error.response?.data?.message
    : undefined;
  return message || fallback;
}

/**
 * 서버 error 응답의 `error` 코드를 꺼낸다 (예: "ACCOUNT_INACTIVE").
 * 분기는 이 코드로 하고, 사용자 문구는 message 를 쓴다.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  return axios.isAxiosError<{ error?: string }>(error)
    ? error.response?.data?.error
    : undefined;
}

/**
 * API 응답 데이터를 Zod 스키마로 검증하는 유틸리티 함수
 * @param schema - Zod 스키마
 * @param data - 검증할 데이터
 * @param method - HTTP 메서드 (GET, POST, PATCH, PUT, DELETE)
 * @param endpoint - API 엔드포인트 (에러 메시지용)
 * @param params - 요청 파라미터 (선택적, 디버깅용)
 * @returns 검증된 데이터
 */
export function validateResponse<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  method: HttpMethod,
  endpoint: string,
  params?: unknown,
): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    const paramsInfo = params ? ` | params: ${JSON.stringify(params)}` : "";
    console.error(
      `[${method}] ${endpoint}${paramsInfo} 데이터 형식 오류:`,
      result.error,
    );
    throw new Error(
      `[${method}] ${endpoint} 응답 데이터 형식이 올바르지 않습니다`,
    );
  }
  return result.data;
}
